'use client';

import { useState, useEffect } from 'react';
import { Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useActiveDomain } from '@/lib/hooks/useActiveDomain';
import { GoalSettingForm } from './GoalSettingForm';

interface Goal {
  id: string;
  goal_type: string;
  is_active: boolean;
}

export function GoalsButton() {
  const { activeDomain } = useActiveDomain();
  const [isOpen, setIsOpen] = useState(false);
  const [goalCount, setGoalCount] = useState(0);

  const domainId = activeDomain?.domain_id;

  useEffect(() => {
    if (!domainId) return;

    const fetchGoalCount = async () => {
      try {
        const res = await fetch(`/api/momento/goals?domainId=${domainId}`);
        if (res.ok) {
          const data = await res.json();
          setGoalCount(data.goals?.length || 0);
        }
      } catch (error) {
        console.error('[GoalsButton] Failed to fetch goals:', error);
      }
    };

    fetchGoalCount();
  }, [domainId]);

  const handleClose = () => {
    setIsOpen(false);
    if (domainId) {
      fetch(`/api/momento/goals?domainId=${domainId}`)
        .then(res => res.json())
        .then(data => setGoalCount(data.goals?.length || 0))
        .catch(() => {});
    }
  };

  if (!domainId) return null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 gap-1.5 text-muted-foreground hover:text-foreground hover:bg-muted relative"
          title="Content Goals"
        >
          <Target className="w-4 h-4" />
          <span className="text-xs hidden sm:inline">Goals</span>
          {goalCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">
              {goalCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <GoalSettingForm
          domainId={domainId}
          onClose={handleClose}
        />
      </PopoverContent>
    </Popover>
  );
}
